import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Sparkles, Check, ArrowRight, Lock, RefreshCw } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BRANCH_META } from "@/lib/branches";

export default function PaywallModal({ open, onClose, reason }) {
    const { refresh } = useAuth();
    const [plan, setPlan] = useState(null);
    const [busy, setBusy] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [syncResult, setSyncResult] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (open) {
            api.get("/payments/plan").then((r) => setPlan(r.data));
        }
    }, [open]);

    const startCheckout = async () => {
        setBusy(true);
        setError(null);
        try {
            const { data } = await api.post("/payments/checkout", {
                origin_url: window.location.origin,
            });
            window.location.href = data.checkout_url;
        } catch (e) {
            setError(e.response?.data?.detail || "Could not start checkout");
            setBusy(false);
        }
    };

    const syncSubscription = async () => {
        setSyncing(true);
        setSyncResult(null);
        try {
            const { data } = await api.post("/payments/sync-subscription");
            await refresh();
            if (data.has_access) {
                setSyncResult({ ok: true, message: "Subscription found — welcome back!" });
                setTimeout(() => (window.location.href = "/atlas"), 1200);
            } else {
                setSyncResult({
                    ok: false,
                    message:
                        "We couldn't find an active subscription for this email on Stripe. If you paid with a different email, subscribe below and we'll merge them.",
                });
            }
        } catch {
            setSyncResult({ ok: false, message: "Sync failed. Please try again in a moment." });
        } finally {
            setSyncing(false);
        }
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-md p-4"
                    data-testid="paywall-modal"
                >
                    <motion.div
                        initial={{ scale: 0.9, y: 20, opacity: 0 }}
                        animate={{ scale: 1, y: 0, opacity: 1 }}
                        exit={{ scale: 0.95, opacity: 0 }}
                        transition={{ type: "spring", stiffness: 220, damping: 22 }}
                        className="crystal rounded-3xl max-w-md w-full p-8 relative"
                    >
                        {onClose && (
                            <button
                                onClick={onClose}
                                className="absolute top-4 right-4 text-slate-400 hover:text-slate-900"
                                data-testid="paywall-close"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        )}

                        <div className="flex justify-center">
                            <div
                                className="w-16 h-16 rounded-full flex items-center justify-center"
                                style={{
                                    background: `linear-gradient(135deg, ${BRANCH_META.arts.color}, ${BRANCH_META.humanities.color})`,
                                    boxShadow: `0 0 40px ${BRANCH_META.humanities.color}55`,
                                }}
                            >
                                <Sparkles className="w-8 h-8 text-white" />
                            </div>
                        </div>

                        <p
                            className="chip mx-auto mt-4 w-fit"
                            style={{ background: BRANCH_META.humanities.soft, color: BRANCH_META.humanities.text }}
                        >
                            <Lock className="w-3 h-3" /> Atlas Access
                        </p>

                        <h2 className="font-display font-black text-3xl tracking-tighter text-slate-900 text-center mt-3 leading-tight">
                            Keep building.
                            <br />
                            <span className="text-humanities">Two dollars a month.</span>
                        </h2>

                        <p className="text-slate-600 text-center text-sm mt-2">
                            {reason ||
                                "Your first micro-quest is complete. To keep contributing, joining quests and levelling up, unlock Atlas Access."}
                        </p>

                        <div className="mt-5 crystal rounded-2xl p-4">
                            <div className="flex items-baseline gap-1">
                                <span className="font-display font-black text-5xl tracking-tighter text-slate-900">
                                    ${((plan?.amount ?? 200) / 100).toFixed(0)}
                                </span>
                                <span className="text-slate-500 font-display font-bold">
                                    /{plan?.interval || "month"}
                                </span>
                                <span className="ml-auto text-xs uppercase tracking-widest font-bold text-slate-400">
                                    Cancel anytime
                                </span>
                            </div>
                            <ul className="mt-4 space-y-1.5 text-sm text-slate-700">
                                {[
                                    "Ignite unlimited beacons across all 130+ disciplines",
                                    "Contribute to Workshop canvases and the Live Ledger",
                                    "Join Global Quests and earn merit badges",
                                    "Public shareable Living Resume Ledger",
                                    "Every dollar funds Atlas discipline stewardship",
                                ].map((f, i) => (
                                    <li key={i} className="flex items-start gap-2">
                                        <Check className="w-4 h-4 mt-0.5 flex-shrink-0" color={BRANCH_META.trades.color} />
                                        <span>{f}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {error && (
                            <div className="mt-3 text-sm text-white bg-sciences px-3 py-2 rounded-xl" data-testid="paywall-error">
                                {error}
                            </div>
                        )}

                        <button
                            disabled={busy}
                            onClick={startCheckout}
                            className="arcade-btn w-full mt-5"
                            data-variant="primary"
                            data-testid="paywall-checkout"
                        >
                            {busy ? "Opening checkout…" : "Subscribe with Stripe"}
                            <ArrowRight className="w-4 h-4" />
                        </button>

                        {/* "Already paid?" — reconcile from Stripe for users who
                            subscribed on another device and are hitting the paywall here. */}
                        <div className="mt-4 pt-4 border-t border-slate-200/70 text-center">
                            <p className="text-xs text-slate-500">
                                Already paid on another device?
                            </p>
                            <button
                                type="button"
                                onClick={syncSubscription}
                                disabled={syncing}
                                className="mt-1 text-sm font-display font-bold text-humanities hover:text-slate-900 inline-flex items-center gap-1"
                                data-testid="paywall-sync-subscription"
                            >
                                <RefreshCw className={`w-3 h-3 ${syncing ? "animate-spin" : ""}`} />
                                {syncing ? "Checking Stripe…" : "Refresh my subscription status"}
                            </button>
                            {syncResult && (
                                <p
                                    className={`text-xs mt-2 ${
                                        syncResult.ok ? "text-trades" : "text-slate-500"
                                    }`}
                                    data-testid="paywall-sync-result"
                                >
                                    {syncResult.message}
                                </p>
                            )}
                        </div>

                        <p className="text-[11px] text-slate-400 text-center mt-3 font-mono">
                            Secured by Stripe. Test card: 4242 4242 4242 4242
                        </p>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
